import isNil from 'lodash/isNil'
import reverse from 'lodash/reverse'
import sortBy from 'lodash/sortBy'
import { createSelector } from 'reselect'

import webSocket from '../../services/api/webSocket'
import { StateType } from '../types'
import { LiveOrderType, OrderType } from './types'

const getLiveRaw = (state: StateType): LiveOrderType[] => state.order.live

const sort = (list: LiveOrderType[]) => sortBy(list, ['orderID'])

// technically open orders can be open, partial or pending!
const isOpen = (order: OrderType | LiveOrderType) =>
  order.ordStatus === webSocket.order.status.OPEN ||
  order.ordStatus === webSocket.order.status.PENDING ||
  order.ordStatus === webSocket.order.status.PARTIAL

const isOpenMargin = (order: OrderType) => isOpen(order) && order.marginOrder

const isFilled = (order: OrderType) => order.ordStatus === webSocket.order.status.FILLED

const getLive = createSelector(getLiveRaw, (state) => sort(reverse(state)))

type TransformedPartialType = {
  avgPx?: number
  cumQty: number
  fee: number
  leavesQty: number
  ordStatus: string
  ordType: string
  orderID: string | number
  orderQty: number
  price: number
  sendingTime: string | number
  side: string
  symbol: string
}

const getLivePartials = (state: StateType) =>
  getLiveRaw(state).reduce((accum: TransformedPartialType[], rawOrder) => {
    const sendingTime = !isNil(rawOrder.transactTime)
      ? new Date(rawOrder.transactTime).getTime()
      : 0
    if (rawOrder.ordStatus === 'partial') {
      accum.push({
        avgPx: isNil(rawOrder.avgPx) ? 0 : rawOrder.avgPx,
        cumQty: rawOrder.cumQty,
        fee: isNil(rawOrder.fee) ? 0 : rawOrder.fee,
        leavesQty: rawOrder.leavesQty,
        ordStatus: rawOrder.ordStatus.toLocaleUpperCase(),
        ordType: rawOrder.ordType.toLocaleUpperCase(),
        orderID: +rawOrder.orderID,
        orderQty: rawOrder.orderQty,
        price: rawOrder.price,
        sendingTime,
        side: rawOrder.side.toLocaleUpperCase(),
        symbol: rawOrder.symbol
      })
    }
    return accum
  }, [])

const getOpenOrders = createSelector(getLive, (state) => state.filter(isOpen))

const getOpenMarginOrders = createSelector(getLive, (state) => state.filter(isOpenMargin))

const getLiveNotOpenOrFilled = createSelector(getLiveRaw, (state) =>
  state.filter((o) => !isOpen(o) && !isFilled(o))
)

const getOpenMarginPositions = (state: StateType) => state.order.positions

const hasOpenOrders = (state: StateType) => getOpenOrders(state).length > 0

const isLoading = (state: StateType) => state.order.subscribed === false

const isSubscribed = (state: StateType) => state.order.subscribed === true

const gotSnapshot = (state: StateType) => state.order.snapshotReceived

const getMarginOrderDetails = (state: StateType) => state.order.marginOrderDetails

const getMarginCallDetails = (state: StateType) => state.order.marginCallDetails

const getSelectedPosition = (state: StateType) => state.order.activePosition

export default {
  getLive,
  getLiveNotOpenOrFilled,
  getLivePartials,
  getMarginCallDetails,
  getMarginOrderDetails,
  getOpenMarginOrders,
  getOpenMarginPositions,
  getOpenOrders,
  getSelectedPosition,
  gotSnapshot,
  hasOpenOrders,
  isLoading,
  isSubscribed
}
